import { Component, computed, signal, OnInit, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TreeNode } from 'primeng/api';
import { Observable, finalize, forkJoin, from, map, of } from 'rxjs';
import { switchMap, catchError, concatMap, toArray } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import _ from 'lodash';
import { AuthService, GlobalMessageUtil } from '@card-software/nexus-utilities';
import { BreadcrumbService, EnumObjectExtended, EnumValues, GlobalEntityDefinition, ToleranceMeterSegment } from '@card-software/library';
import { Fabric } from '../../../models/fabric/fabric';
import {
  FinishedGoodsTransfer,
  FinishedGoodsTransferInsert,
  FinishedGoodsTransferStatus,
  FinishedGoodTransferCuadraje,
  FinishedGoodTransferCuadrajeItem,
} from '../../../models/inventory/finished-goods-transfer';
import { FinishingRoute } from '../../../models/production/finishing-route';
import { LocationNode } from '../../../models/location';
import { PackingType, WorkOrder } from '../../../models/order/work-order';
import { ProductionCard } from '../../../models/production/production-card';
import { Roll } from '../../../models/production/roll';
import { UserGroup } from '../../../models/authorization/enum/user-group.enum';
import { EnumService } from '../../../services/enum.service';
import { FinishedGoodsTransferProxyService } from '../../../proxies/inventory/finished-goods-transfer.proxy.service';
import { FinishingRouteProxyService } from '../../../proxies/production/finishing-route.proxy.service';
import { LabDipRequestProxyService, Color } from '../../../proxies/item/lab-dip-request.proxy.service';
import { LocationProxyService } from '../../../proxies/inventory/location.proxy.service';
import { InventoryReportProxyService } from '../../../proxies/inventory/inventory-report.proxy.service';
import { ProductionCardsProxyService } from '../../../proxies/production/production-card.proxy.service';
import { RollProxyService } from '../../../proxies/production/roll.proxy';
import { WorkOrderProxyService } from '../../../proxies/order/work-order.proxy.service';

const ROLLING_PROCESS_ID = '29';
const ROLLING_LOCATION_CODE = 'NT.WH-RG-1';
const FINISHING_LOCATION_CODE = 'NT.WH-FIN-1';
const FG_WAREHOUSE_LOCATION_CODE = 'NT.WH-FG-1';
const SOURCE_LOCATION_CODES = [ROLLING_LOCATION_CODE, FINISHING_LOCATION_CODE];
const LBS_TO_KG = 0.453592;

@Component({
  selector: 'app-finished-good-transfer-details',
  templateUrl: './finished-good-transfer-details.component.html',
  styleUrl: './finished-good-transfer-details.component.scss',
})
export class FinishedGoodTransferDetailsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly enumService = inject(EnumService);
  private readonly finishedGoodsTransferProxyService = inject(FinishedGoodsTransferProxyService);
  private readonly inventoryReportProxyService = inject(InventoryReportProxyService);
  private readonly productionCardProxyService = inject(ProductionCardsProxyService);
  private readonly workOrderProxyService = inject(WorkOrderProxyService);
  private readonly labDipRequestProxyService = inject(LabDipRequestProxyService);
  private readonly rollProxyService = inject(RollProxyService);
  private readonly locationProxyService = inject(LocationProxyService);
  private readonly finishingRouteProxyService = inject(FinishingRouteProxyService);
  private readonly translateService = inject(TranslateService);
  private readonly breadcrumbService = inject(BreadcrumbService);

  readonly Status = FinishedGoodsTransferStatus;

  productionCard: FormControl<ProductionCard | null> = new FormControl<ProductionCard | null>(null);
  status: FormControl<number | null> = new FormControl<number | null>(0);

  statusEnum: EnumObjectExtended;
  fabricFormValues: EnumValues;
  rollCategoryValues: EnumValues;
  categoryFilterOptions: { label: string; value: number }[] = [];

  originalFinishedGoodsTransfer?: FinishedGoodsTransfer;

  loading = signal<number>(0);
  readonly = signal<boolean>(false);

  private candidateRolls = signal<Roll[]>([]);
  private rollNumbersInOtherTransfers = signal<string[]>([]);
  loadedRollNumbers = signal<string[]>([]);
  private transferRolls = signal<Roll[]>([]);

  workOrder = signal<WorkOrder | null>(null);
  private colorCache = signal<Map<string, Color>>(new Map());
  liveCuadraje = signal<FinishedGoodTransferCuadraje | null>(null);

  private allSourceLocationOptions = signal<{ label: string; value: string }[]>([]);
  private excludedLocationCodes = signal<string[]>([]);
  selectedSourceLocation = signal<string | null>(null);

  locationTreeNodes = signal<TreeNode[]>([]);
  selectedLocationNode = signal<TreeNode | null>(null);
  isLoadingLocations = signal<boolean>(false);
  showLocationSelector = signal<boolean>(false);
  private pendingStatusChange = signal<number | null>(null);

  missingRollsCategoryFilter = signal<number | null>(null);
  missingRollsQualityFilter = signal<boolean | null>(null);
  selectedMissingRollNumbers = signal<Set<string>>(new Set<string>());

  sourceLocationOptions = computed(() => {
    const all = this.allSourceLocationOptions();
    const excluded = this.excludedLocationCodes();
    return all.filter((opt) => !excluded.includes(opt.value));
  });

  // A roll already loaded in THIS transfer stays available: the backend "in other transfers" list has no
  // knowledge of the current transfer, so without this it would be double-subtracted in totalMissingRolls.
  private availableRolls = computed<Roll[]>(() => {
    const inOtherTransfers = this.rollNumbersInOtherTransfers();
    const loadedNumbers = this.loadedRollNumbers();
    return this.candidateRolls().filter((roll) => loadedNumbers.includes(roll.number!) || !inOtherTransfers.includes(roll.number!));
  });

  private rollsInAnotherFgt = computed<Roll[]>(() => {
    const inOtherTransfers = this.rollNumbersInOtherTransfers();
    const loadedNumbers = this.loadedRollNumbers();
    return this.candidateRolls().filter((roll) => inOtherTransfers.includes(roll.number!) && !loadedNumbers.includes(roll.number!));
  });

  loadedRolls = computed<Roll[]>(() => {
    if (this.transferRolls().length > 0) {
      return this.transferRolls();
    }
    const loadedNumbers = this.loadedRollNumbers();
    return this.candidateRolls().filter((roll) => loadedNumbers.includes(roll.number!));
  });

  missingRolls = computed<Roll[]>(() => {
    const loadedNumbers = this.loadedRollNumbers();
    return this.availableRolls().filter((roll) => !loadedNumbers.includes(roll.number!));
  });

  filteredMissingRolls = computed<Roll[]>(() => {
    const category = this.missingRollsCategoryFilter();
    const quality = this.missingRollsQualityFilter();
    return this.missingRolls().filter((roll) => {
      if (category !== null && roll.rollCategory !== category) return false;
      if (quality !== null && (roll.finalQC?.isApproved ?? null) !== quality) return false;
      return true;
    });
  });

  totalRolls = computed(() => this.candidateRolls().length);
  totalAvailableRolls = computed(() => this.availableRolls().length);
  totalLoadedRolls = computed(() => this.loadedRollNumbers().length);
  totalMissingRolls = computed(() => Math.max(0, this.totalAvailableRolls() - this.totalLoadedRolls()));
  totalRollsInAnotherFgt = computed(() => this.rollsInAnotherFgt().length);
  selectedMissingRollsCount = computed(() => this.selectedMissingRollNumbers().size);

  exceedsTolerance = computed(() => this.liveCuadraje()?.items.some((item) => item.exceedsTolerance) ?? false);

  allFilteredMissingRollsSelected = computed<boolean>(() => {
    const filtered = this.filteredMissingRolls();
    if (filtered.length === 0) return false;
    const selected = this.selectedMissingRollNumbers();
    return filtered.every((roll) => selected.has(roll.number));
  });

  loadedRollsTotalWeightLbs = computed(() => this.loadedRolls().reduce((sum, roll) => sum + (roll.finalWeight?.weightInLbs ?? 0), 0));
  loadedRollsTotalWeightKgs = computed(() => this.lbsToKg(this.loadedRollsTotalWeightLbs()));
  missingRollsTotalWeightLbs = computed(() => this.missingRolls().reduce((sum, roll) => sum + (roll.finalWeight?.weightInLbs ?? 0), 0));

  filteredMissingRollsTotalWeightLbs = computed(() =>
    this.filteredMissingRolls().reduce((sum, roll) => sum + (roll.finalWeight?.weightInLbs ?? 0), 0),
  );

  filteredMissingRollsTotalWeightKgs = computed(() => this.lbsToKg(this.filteredMissingRollsTotalWeightLbs()));

  get isLoading(): boolean {
    return this.loading() > 0;
  }

  get qualityFilterOptions(): { label: string; value: boolean }[] {
    return [
      { label: this.translate('Approved', 'Approved'), value: true },
      { label: this.translate('Rejected', 'Rejected'), value: false },
    ];
  }

  get roleStatusOptions(): EnumValues {
    const statuses = this.statusEnum.values;
    if (!statuses || typeof statuses !== 'object') return {};

    const currentStatus = this.originalFinishedGoodsTransfer?.status ?? this.status.value ?? FinishedGoodsTransferStatus.Draft;
    const filtered: EnumValues = {};

    if (currentStatus === FinishedGoodsTransferStatus.Received || currentStatus === FinishedGoodsTransferStatus.Voided) {
      filtered[currentStatus] = statuses[currentStatus];
      return filtered;
    }

    const allowedStatusesByRole = new Set<number>();

    if (AuthService.hasAnyRole([UserGroup.FinishedGoodsTransferEditor, UserGroup.FinishedGoodsTransferAdmin])) {
      allowedStatusesByRole.add(FinishedGoodsTransferStatus.Draft);
      allowedStatusesByRole.add(FinishedGoodsTransferStatus.ReadyForTransfer);
      allowedStatusesByRole.add(FinishedGoodsTransferStatus.InRoute);
      allowedStatusesByRole.add(FinishedGoodsTransferStatus.Voided);
    }

    if (AuthService.hasAnyRole([UserGroup.FinishedGoodsTransferReceiver, UserGroup.FinishedGoodsTransferAdmin])) {
      allowedStatusesByRole.add(FinishedGoodsTransferStatus.Received);
    }

    const allowedStatusesByTransition = this.allowedStatusTransitions(currentStatus) ?? [...allowedStatusesByRole];

    for (const status of [...allowedStatusesByRole].sort()) {
      if (allowedStatusesByTransition.includes(status) && statuses[status]) {
        filtered[status] = statuses[status];
      }
    }

    return filtered;
  }

  constructor() {
    this.statusEnum = this.enumService.getEnum('FinishedGoodTransferStatus', 'inventory');
    this.fabricFormValues = this.enumService.getEnum('FabricForm', 'item').values;
    this.rollCategoryValues = this.enumService.getEnum('RollCategory', 'production').values;

    this.categoryFilterOptions = Object.entries(this.rollCategoryValues).map(([key, label]) => ({
      label: label as string,
      value: parseInt(key, 10),
    }));
  }

  ngOnInit(): void {
    this.loadSourceLocationOptions();

    this.route.queryParams.subscribe((params) => {
      const id = params['id'];
      if (_.isNil(id)) {
        this.router.navigate(['/finished-good-transfers']);
      } else if (id !== 'new') {
        this.populateFinishedGoodsTransfer(id);
      } else {
        this.breadcrumbService.setCurrent('Common_New');
      }
    });

    this.status.valueChanges.subscribe((value) => {
      if (value === null || !this.originalFinishedGoodsTransfer) {
        return;
      }
      if (value === FinishedGoodsTransferStatus.Received) {
        this.pendingStatusChange.set(value);
        this.showLocationSelector.set(true);
        this.loadLocationTree();
      } else if (value === FinishedGoodsTransferStatus.ReadyForTransfer && this.exceedsTolerance()) {
        // The transfer is not blocked: the dropdown is reverted so the user reads the Live Cuadraje
        // warning first, and the card's own button calls updateStatus to continue.
        this.status.setValue(this.originalFinishedGoodsTransfer.status, { emitEvent: false });
        GlobalMessageUtil.warn(
          this.translate('FGT_Tolerance_Warning', 'Tolerance'),
          this.translate(
            'FGT_Tolerance_Warning_Detail',
            'This transfer exceeds the customer tolerance. Review the Live Cuadraje notice or press "Continue anyway".',
          ),
        );
      } else {
        this.updateStatus(value);
      }
    });
  }

  cancel(): void {
    this.router.navigate(['/finished-good-transfers']);
  }

  // TODO: restore `statuses: [ProductionCardStatus.Finishing]` in the search once the initial rollout week is over.
  public fetchProductionCards = (query: string): Observable<ProductionCard[]> =>
    this.productionCardProxyService.search({ productionCardNumber: query }).pipe(map((response) => response.data ?? []));

  onProductionCardSelected(): void {
    const pc = this.productionCard.value;
    if (!pc?.workOrderReference || !pc?.id || this.originalFinishedGoodsTransfer) return;

    this.increaseLoading();
    forkJoin({
      workOrder: this.workOrderProxyService.getById(pc.workOrderReference),
      finishingRoutes: this.finishingRouteProxyService
        .getByProductionCardId(pc.id)
        .pipe(catchError(() => of({ success: false, data: [] as FinishingRoute[] }))),
      rollingCandidates: this.productionCardProxyService
        .getFinishedGoodsCandidateRolls(pc.id, ROLLING_LOCATION_CODE)
        .pipe(catchError(() => of({ success: false, data: [] as Roll[] }))),
      finishingCandidates: this.productionCardProxyService
        .getFinishedGoodsCandidateRolls(pc.id, FINISHING_LOCATION_CODE)
        .pipe(catchError(() => of({ success: false, data: [] as Roll[] }))),
    })
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: ({ workOrder, finishingRoutes, rollingCandidates, finishingCandidates }) => {
          if (!workOrder.success || !workOrder.data) {
            return;
          }
          this.workOrder.set(workOrder.data);
          const routes = finishingRoutes.success && finishingRoutes.data ? finishingRoutes.data : [];
          const hasRollsAtRolling = !!(rollingCandidates.success && rollingCandidates.data?.length);
          const hasRollsAtFinishing = !!(finishingCandidates.success && finishingCandidates.data?.length);
          this.filterSourceLocationOptions(workOrder.data.fabrics, routes, hasRollsAtRolling, hasRollsAtFinishing);
        },
      });
  }

  insertFinishedGoodsTransfer(): void {
    if (this.productionCard.value === null) {
      GlobalMessageUtil.warn(
        this.translate('Common_Warning', 'Warning'),
        this.translate('FGT_Production_Card_Required', 'Production card is required'),
      );
      return;
    }

    const sourceLocation = this.selectedSourceLocation();
    if (!sourceLocation) {
      GlobalMessageUtil.warn(
        this.translate('Common_Warning', 'Warning'),
        this.translate('FGT_Source_Location_Required', 'Source location is required'),
      );
      return;
    }

    const finishedGoodsTransfer: FinishedGoodsTransferInsert = {
      status: this.status.value!,
      initiator: {
        reference: this.productionCard.value!.id!,
        entityDefinitionCode: GlobalEntityDefinition.ProductionCard,
        number: this.productionCard.value!.productionCardNumber!,
      },
      rollNumbers: [],
      fromLocationCode: sourceLocation,
    };

    this.increaseLoading();
    this.finishedGoodsTransferProxyService
      .insert(finishedGoodsTransfer)
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: (response) => {
          if (!response.success || !response.data) {
            return;
          }
          GlobalMessageUtil.success(
            this.translate('Common_Success', 'Success'),
            this.translate('FGT_Created_Successfully', 'Finished goods transfer {{number}} created successfully', {
              number: response.data.number,
            }),
          );
          this.router.navigate(['/finished-good-transfer-details'], {
            queryParams: {
              id: response.data.id,
            },
          });
        },
      });
  }

  continueDespiteTolerance(): void {
    this.updateStatus(FinishedGoodsTransferStatus.ReadyForTransfer);
  }

  segments(item: FinishedGoodTransferCuadrajeItem): ToleranceMeterSegment[] {
    return [
      {
        label: this.translate('Already_In_Finished_Goods', 'Already in Finished Goods'),
        value: item.alreadyInFinishedGoodsQuantityInPounds,
        color: 'var(--p-green-500)',
      },
      {
        label: this.translate('This_Transfer', 'This transfer'),
        value: item.thisTransferQuantityInPounds,
        color: 'var(--primary-color)',
      },
    ];
  }

  toleranceMax(item: FinishedGoodTransferCuadrajeItem): number {
    return item.clientTolerancePercentage != null
      ? item.requiredQuantityInPounds * (1 + item.clientTolerancePercentage / 100)
      : Number.MAX_SAFE_INTEGER;
  }

  // clientShortagePercentage is typically negative (e.g. -3), so this already reads as a floor below the objective.
  toleranceMin(item: FinishedGoodTransferCuadrajeItem): number {
    return item.clientShortagePercentage != null ? item.requiredQuantityInPounds * (1 + item.clientShortagePercentage / 100) : 0;
  }

  scaleMax(item: FinishedGoodTransferCuadrajeItem): number {
    return Math.max(this.toleranceMax(item), item.projectedQuantityInPounds);
  }

  fabricTypeLabel(fabricCode: string): string {
    return this.fabricFormValues[this.getFabricByCode(fabricCode)?.fabricForm ?? 0];
  }

  getFabricByCode(fabricCode: string): Fabric | null {
    const workOrder = this.workOrder();
    return workOrder?.fabrics.find((f) => f.fabricCode === fabricCode)?.fabric || null;
  }

  getColorName(colorReference: string): string {
    const cached = this.colorCache().get(colorReference);
    return cached?.name ?? colorReference;
  }

  getSourceLocationLabel(locationCode: string | undefined | null): string {
    if (!locationCode) return '';
    const option = this.sourceLocationOptions().find((o) => o.value === locationCode);
    return option?.label ?? locationCode;
  }

  canEdit(): boolean {
    return AuthService.hasAnyRole([UserGroup.FinishedGoodsTransferEditor, UserGroup.FinishedGoodsTransferAdmin]);
  }

  lbsToKg(lbs: number): number {
    return lbs * LBS_TO_KG;
  }

  updateStatus(newStatus: number): void {
    if (newStatus === this.originalFinishedGoodsTransfer!.status) {
      return;
    }
    this.increaseLoading();
    this.finishedGoodsTransferProxyService
      .updateStatus(this.originalFinishedGoodsTransfer!.id!, newStatus)
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: () => {
          GlobalMessageUtil.success(
            this.translate('Common_Success', 'Success'),
            this.translate('FGT_Status_Updated', 'Finished goods transfer {{number}} status updated', {
              number: this.originalFinishedGoodsTransfer!.number,
            }),
          );
          this.originalFinishedGoodsTransfer!.status = newStatus;
          this.status.setValue(this.originalFinishedGoodsTransfer!.status, { emitEvent: false });

          if (newStatus !== FinishedGoodsTransferStatus.Draft) {
            this.readonly.set(true);
          }
        },
        error: () => {
          this.status.setValue(this.originalFinishedGoodsTransfer!.status, { emitEvent: false });
        },
      });
  }

  onLocationSelected(node: TreeNode | null): void {
    this.selectedLocationNode.set(node);
  }

  confirmReceiveWithLocation(): void {
    const selectedNode = this.selectedLocationNode();
    if (!selectedNode || !selectedNode.key) {
      GlobalMessageUtil.warn(
        this.translate('Common_Warning', 'Warning'),
        this.translate('FGT_Select_Destination_Location', 'Please select a destination location'),
      );
      return;
    }

    const pendingStatus = this.pendingStatusChange();
    if (pendingStatus === null) return;

    const toLocationCode = selectedNode.key as string;

    this.increaseLoading();
    this.finishedGoodsTransferProxyService
      .updateStatus(this.originalFinishedGoodsTransfer!.id!, pendingStatus, toLocationCode)
      .pipe(
        finalize(() => {
          this.decreaseLoading();
          this.resetLocationSelector();
        }),
      )
      .subscribe({
        next: () => {
          GlobalMessageUtil.success(
            this.translate('Common_Success', 'Success'),
            this.translate('FGT_Received_Successfully', 'Finished goods transfer {{number}} received successfully', {
              number: this.originalFinishedGoodsTransfer!.number,
            }),
          );
          this.originalFinishedGoodsTransfer!.status = pendingStatus;
          this.status.setValue(this.originalFinishedGoodsTransfer!.status, { emitEvent: false });
          this.readonly.set(true);
        },
        error: () => {
          this.status.setValue(this.originalFinishedGoodsTransfer!.status, { emitEvent: false });
        },
      });
  }

  cancelReceive(): void {
    this.status.setValue(this.originalFinishedGoodsTransfer!.status, { emitEvent: false });
    this.resetLocationSelector();
  }

  addRollToTransaction(rollNumber: string): void {
    const availableRoll = this.availableRolls().find((r) => r.number === rollNumber);
    if (!availableRoll) {
      GlobalMessageUtil.warn(
        this.translate('FGT_Invalid_Roll', 'Invalid Roll'),
        this.translate('FGT_Roll_Not_Available', 'Roll {{roll}} is not available for this finished goods transfer', { roll: rollNumber }),
      );
      return;
    }

    if (this.loadedRollNumbers().includes(rollNumber)) {
      GlobalMessageUtil.warn(
        this.translate('FGT_Already_Added', 'Already Added'),
        this.translate('FGT_Roll_Already_Added', 'Roll {{roll}} has already been added', { roll: rollNumber }),
      );
      return;
    }

    this.increaseLoading();
    this.finishedGoodsTransferProxyService
      .addRoll(this.originalFinishedGoodsTransfer!.id!, rollNumber)
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: (response) => {
          if (!response.success) {
            return;
          }
          this.loadedRollNumbers.update((rolls) => [...rolls, rollNumber]);
          GlobalMessageUtil.success(
            this.translate('FGT_Roll_Added', 'Roll Added'),
            this.translate('FGT_Roll_Added_Detail', 'Roll {{roll}} added successfully', { roll: rollNumber }),
          );
          this.loadLiveCuadraje();
        },
      });
  }

  removeRoll(rollNumber: string): void {
    this.increaseLoading();
    this.finishedGoodsTransferProxyService
      .removeRoll(this.originalFinishedGoodsTransfer!.id!, rollNumber)
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: (response) => {
          if (!response.success) {
            return;
          }
          this.loadedRollNumbers.update((rolls) => rolls.filter((r) => r !== rollNumber));
          GlobalMessageUtil.success(
            this.translate('FGT_Roll_Removed', 'Roll Removed'),
            this.translate('FGT_Roll_Removed_Detail', 'Roll {{roll}} removed successfully', { roll: rollNumber }),
          );
          this.loadLiveCuadraje();
        },
      });
  }

  // Rolls are added sequentially (concatMap) to avoid concurrent-update conflicts on the transfer document.
  addSelectedRolls(): void {
    const missingNumbers = new Set(this.missingRolls().map((roll) => roll.number));
    const toAdd = Array.from(this.selectedMissingRollNumbers()).filter((rollNumber) => missingNumbers.has(rollNumber));

    if (toAdd.length === 0) {
      GlobalMessageUtil.warn(
        this.translate('FGT_No_Selection', 'No Selection'),
        this.translate('FGT_Select_At_Least_One_Roll', 'Please select at least one roll to add'),
      );
      return;
    }

    this.increaseLoading();
    from(toAdd)
      .pipe(
        concatMap((rollNumber) =>
          this.finishedGoodsTransferProxyService.addRoll(this.originalFinishedGoodsTransfer!.id!, rollNumber).pipe(
            map((response) => ({ rollNumber, success: !!response.success })),
            catchError(() => of({ rollNumber, success: false })),
          ),
        ),
        toArray(),
        finalize(() => this.decreaseLoading()),
      )
      .subscribe({
        next: (results) => {
          const added = results.filter((r) => r.success).map((r) => r.rollNumber);
          const failed = results.filter((r) => !r.success).map((r) => r.rollNumber);

          if (added.length > 0) {
            this.loadedRollNumbers.update((rolls) => [...rolls, ...added]);
            this.selectedMissingRollNumbers.update((set) => {
              const next = new Set(set);
              added.forEach((rollNumber) => next.delete(rollNumber));
              return next;
            });
            GlobalMessageUtil.success(
              this.translate('FGT_Rolls_Added', 'Rolls Added'),
              this.translate('FGT_Rolls_Added_Detail', '{{count}} roll(s) added successfully', { count: added.length }),
            );
            this.loadLiveCuadraje();
          }
          if (failed.length > 0) {
            GlobalMessageUtil.warn(
              this.translate('FGT_Some_Rolls_Not_Added', 'Some Rolls Not Added'),
              this.translate('FGT_Rolls_Not_Added_Detail', 'Failed to add: {{rolls}}', { rolls: failed.join(', ') }),
            );
          }
        },
      });
  }

  isMissingRollSelected(rollNumber: string): boolean {
    return this.selectedMissingRollNumbers().has(rollNumber);
  }

  toggleMissingRollSelection(rollNumber: string, checked: boolean): void {
    this.selectedMissingRollNumbers.update((set) => {
      const next = new Set(set);
      if (checked) {
        next.add(rollNumber);
      } else {
        next.delete(rollNumber);
      }
      return next;
    });
  }

  selectAllFilteredMissingRolls(): void {
    const numbers = this.filteredMissingRolls().map((roll) => roll.number);
    this.selectedMissingRollNumbers.set(new Set(numbers));
  }

  clearMissingRollsSelection(): void {
    this.selectedMissingRollNumbers.set(new Set<string>());
  }

  toggleSelectAllFilteredMissingRolls(checked: boolean): void {
    if (checked) {
      this.selectAllFilteredMissingRolls();
    } else {
      this.clearMissingRollsSelection();
    }
  }

  clearFilters(): void {
    this.missingRollsCategoryFilter.set(null);
    this.missingRollsQualityFilter.set(null);
  }

  downloadReport(): void {
    if (!this.originalFinishedGoodsTransfer?.id) {
      GlobalMessageUtil.error(
        this.translate('Common_Error', 'Error'),
        this.translate('FGT_Id_Required', 'Finished goods transfer ID is required'),
      );
      return;
    }

    this.increaseLoading();
    this.inventoryReportProxyService
      .productionTransferReport(this.originalFinishedGoodsTransfer.id, 'finished-good-transfer')
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: (blob) => {
          const transferNumber = this.originalFinishedGoodsTransfer?.number ?? 'FinishedGoodsTransfer';
          this.saveBlobAsFile(blob, `FinishedGoodsTransfer_${transferNumber}_${new Date().getTime()}.pdf`);
          GlobalMessageUtil.success(
            this.translate('Common_Success', 'Success'),
            this.translate('FGT_Report_Downloaded', 'Report downloaded successfully'),
          );
        },
      });
  }

  private populateFinishedGoodsTransfer(id: string): void {
    this.increaseLoading();
    this.finishedGoodsTransferProxyService
      .getById(id)
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: (response) => {
          if (!response.success || !response.data) {
            return;
          }

          this.originalFinishedGoodsTransfer = response.data;
          this.breadcrumbService.setCurrent(this.originalFinishedGoodsTransfer.number);
          this.status.setValue(this.originalFinishedGoodsTransfer.status);
          this.loadedRollNumbers.set(this.originalFinishedGoodsTransfer.rollNumbers || []);

          if (this.originalFinishedGoodsTransfer.fromLocationCode) {
            this.selectedSourceLocation.set(this.originalFinishedGoodsTransfer.fromLocationCode);
          }

          if (this.originalFinishedGoodsTransfer.status !== FinishedGoodsTransferStatus.Draft) {
            this.readonly.set(true);
          }

          this.loadInitiatorProductionCard(this.originalFinishedGoodsTransfer.initiator.reference);
        },
      });
  }

  private loadInitiatorProductionCard(productionCardId: string): void {
    this.increaseLoading();
    this.productionCardProxyService
      .getById(productionCardId)
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: (response) => {
          if (!response.success || !response.data) {
            return;
          }

          this.productionCard.setValue(response.data);

          if (this.originalFinishedGoodsTransfer!.status === FinishedGoodsTransferStatus.Draft) {
            this.loadCandidateRolls();
          } else {
            this.loadTransferRolls(this.originalFinishedGoodsTransfer!.rollNumbers || []);
          }

          this.loadWorkOrder(response.data.workOrderReference);
          if (response.data.colorReference) {
            this.loadColorName(response.data.colorReference);
          }
          this.loadLiveCuadraje();
        },
      });
  }

  private loadWorkOrder(workOrderReference: string): void {
    if (!workOrderReference) return;

    this.increaseLoading();
    this.workOrderProxyService
      .getById(workOrderReference)
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.workOrder.set(response.data);
          }
        },
      });
  }

  private loadLiveCuadraje(): void {
    const id = this.originalFinishedGoodsTransfer?.id;
    if (!id || this.originalFinishedGoodsTransfer!.status !== FinishedGoodsTransferStatus.Draft) return;

    this.increaseLoading();
    this.finishedGoodsTransferProxyService
      .getCuadraje(id)
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.liveCuadraje.set(response.data);
          }
        },
      });
  }

  private loadColorName(colorReference: string): void {
    if (!colorReference || this.colorCache().has(colorReference)) return;

    this.labDipRequestProxyService
      .getColorsByIds([colorReference])
      // The color reference itself is shown when the lookup fails, so the error is not surfaced.
      .pipe(catchError(() => of({ data: [] as Color[] })))
      .subscribe((response) => {
        const colors = response.data ?? [];
        if (colors.length === 0) return;

        this.colorCache.update((cache) => {
          const next = new Map(cache);
          for (const color of colors) {
            next.set(color.id, color);
          }
          return next;
        });
      });
  }

  private loadCandidateRolls(): void {
    const pc = this.productionCard.value;
    if (!pc?.id) return;

    const sourceLocation = this.selectedSourceLocation();
    if (!sourceLocation) {
      this.candidateRolls.set([]);
      return;
    }

    this.increaseLoading();
    this.productionCardProxyService
      .getFinishedGoodsCandidateRolls(pc.id, sourceLocation)
      .pipe(
        switchMap((response) => {
          if (!response.success || !response.data) {
            return of({ weighedRolls: [], rollsInOtherTransfers: [] as string[] });
          }

          const weighedRolls = response.data.filter((roll) => this.hasBeenWeighed(roll));

          if (response.data.length > 0 && weighedRolls.length === 0) {
            GlobalMessageUtil.warn(
              this.translate('FGT_No_Weighed_Rolls', 'No Weighed Rolls'),
              this.translate(
                'FGT_No_Weighed_Rolls_Detail',
                'There are candidate rolls, but none have been weighed. Please ensure rolls have a final weight before moving to Finished Goods.',
              ),
              6000,
            );
          }

          if (weighedRolls.length === 0) {
            return of({ weighedRolls: [], rollsInOtherTransfers: { rollNumbersInTransfer: [] } });
          }

          const rollNumbers = weighedRolls.map((r) => r.number!);

          return this.finishedGoodsTransferProxyService.getRollsNumbersInTransfer(rollNumbers).pipe(
            map((inTransferResponse) => ({
              weighedRolls,
              rollsInOtherTransfers:
                inTransferResponse.success && inTransferResponse.data ? inTransferResponse.data : { rollNumbersInTransfer: [] },
            })),
            // A 404/error here must not exclude any roll, otherwise every candidate looks unavailable.
            catchError(() => of({ weighedRolls, rollsInOtherTransfers: { rollNumbersInTransfer: [] } })),
          );
        }),
        finalize(() => this.decreaseLoading()),
      )
      .subscribe({
        next: ({ weighedRolls, rollsInOtherTransfers }) => {
          this.candidateRolls.set(weighedRolls);

          const rollNumbersInTransfer = Array.isArray(rollsInOtherTransfers)
            ? rollsInOtherTransfers
            : rollsInOtherTransfers.rollNumbersInTransfer || [];
          this.rollNumbersInOtherTransfers.set(rollNumbersInTransfer);
        },
      });
  }

  private loadTransferRolls(rollNumbers: string[]): void {
    if (!rollNumbers || rollNumbers.length === 0) return;

    this.increaseLoading();
    this.rollProxyService
      .getByRollNumbers(rollNumbers)
      .pipe(finalize(() => this.decreaseLoading()))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.transferRolls.set(response.data);
          }
        },
      });
  }

  private hasBeenWeighed(roll: Roll): boolean {
    const weight = roll.finalWeight?.weightInLbs;
    return !_.isNil(weight) && weight > 0;
  }

  private loadSourceLocationOptions(): void {
    this.locationProxyService.batchGetByCodes(SOURCE_LOCATION_CODES).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const options = response.data.map((loc) => ({
            label: loc.name?.en ?? loc.code,
            value: loc.locationCode,
          }));
          this.allSourceLocationOptions.set(options);
        }
      },
      error: () => {
        this.allSourceLocationOptions.set(SOURCE_LOCATION_CODES.map((code) => ({ label: code, value: code })));
      },
    });
  }

  private filterSourceLocationOptions(
    fabrics: { packingType: number }[],
    finishingRoutes: FinishingRoute[] = [],
    hasRollsAtRolling = false,
    hasRollsAtFinishing = false,
  ): void {
    const hasRollingPackingType = fabrics.some((f) => f.packingType === PackingType.Rolling);
    const hasFoldingPackingType = fabrics.some((f) => f.packingType === PackingType.Folding);

    const hasRouteWithRollingProcess = finishingRoutes.some((route) => this.hasRollingProcess(route));
    const hasRouteWithoutRollingProcess = finishingRoutes.some((route) => !this.hasRollingProcess(route));

    const hasRolling = hasRollingPackingType || hasRouteWithRollingProcess || hasRollsAtRolling;
    const hasFolding = hasFoldingPackingType || hasRouteWithoutRollingProcess;

    const codesToExclude: string[] = [];

    // Confirmed roll presence wins over the theoretical packing-type/route rules, so we never auto-select
    // a location where the API would reject the transfer for having no rolls.
    if (hasRollsAtRolling || hasRollsAtFinishing) {
      if (!hasRollsAtRolling) codesToExclude.push(ROLLING_LOCATION_CODE);
      if (!hasRollsAtFinishing) codesToExclude.push(FINISHING_LOCATION_CODE);
    } else {
      if (!hasRolling) codesToExclude.push(ROLLING_LOCATION_CODE);
      if (!hasFolding) codesToExclude.push(FINISHING_LOCATION_CODE);
    }

    this.excludedLocationCodes.set(codesToExclude);

    const availableOptions = this.sourceLocationOptions();
    if (availableOptions.length === 1) {
      this.selectedSourceLocation.set(availableOptions[0].value);
    }
  }

  private hasRollingProcess(route: FinishingRoute): boolean {
    return !!route.route?.some((process) => process.processId === ROLLING_PROCESS_ID);
  }

  private loadLocationTree(): void {
    this.isLoadingLocations.set(true);
    this.locationProxyService
      .getAllChildrenLocations(FG_WAREHOUSE_LOCATION_CODE)
      .pipe(finalize(() => this.isLoadingLocations.set(false)))
      .subscribe({
        next: (response) => {
          if (response.data) {
            this.locationTreeNodes.set(this.convertLocationsToTreeNodes(response.data));
          }
        },
      });
  }

  private convertLocationsToTreeNodes(locations: LocationNode[]): TreeNode[] {
    const treeNodes: TreeNode[] = [];
    const locationMap = new Map<string, TreeNode>();

    locations.forEach((loc) => {
      const node: TreeNode = {
        key: loc.locationCode,
        label: loc.code + ' - ' + loc.name.en,
        data: loc,
        leaf: true,
        selectable: loc.isStorable,
      };
      locationMap.set(loc.locationCode, node);
    });

    locations.forEach((loc) => {
      const node = locationMap.get(loc.locationCode);
      if (node) {
        if (loc.parentCode) {
          const parentNode = locationMap.get(loc.parentCode);
          if (parentNode) {
            if (!parentNode.children) {
              parentNode.children = [];
              parentNode.leaf = false;
            }
            parentNode.children.push(node);
          } else {
            treeNodes.push(node);
          }
        } else {
          treeNodes.push(node);
        }
      }
    });

    return treeNodes;
  }

  private resetLocationSelector(): void {
    this.showLocationSelector.set(false);
    this.selectedLocationNode.set(null);
    this.pendingStatusChange.set(null);
  }

  private allowedStatusTransitions(currentStatus: number): number[] | null {
    switch (currentStatus) {
      case FinishedGoodsTransferStatus.Draft:
        return [FinishedGoodsTransferStatus.ReadyForTransfer, FinishedGoodsTransferStatus.Voided];
      case FinishedGoodsTransferStatus.ReadyForTransfer:
        return [FinishedGoodsTransferStatus.InRoute, FinishedGoodsTransferStatus.Voided];
      case FinishedGoodsTransferStatus.InRoute:
        return [FinishedGoodsTransferStatus.Received, FinishedGoodsTransferStatus.Voided];
      case FinishedGoodsTransferStatus.Received:
        return [];
      default:
        return null;
    }
  }

  private saveBlobAsFile(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }

  private increaseLoading(): void {
    this.loading.update((n) => n + 1);
  }

  private decreaseLoading(): void {
    this.loading.update((n) => Math.max(0, n - 1));
  }

  private translate(key: string, fallback: string, params?: Record<string, string | number>): string {
    const translation = this.translateService.instant(key, params);
    if (translation !== key) {
      return translation;
    }
    if (!params) {
      return fallback;
    }
    return Object.entries(params).reduce(
      (result, [paramKey, paramValue]) => result.replace(new RegExp(`{{\\s*${paramKey}\\s*}}`, 'g'), String(paramValue)),
      fallback,
    );
  }
}
